import { css } from '@emotion/react';
import styled from '@emotion/styled';
import React from 'react';
import {
  ComponentColors,
  ComponentSize,
  ComponentWithActiveStateMode,
  ComponentWithColorMode,
  ComponentWithDisabledStateMode,
  ComponentWithSizeMode,
  getComponentColors,
  lineHeightMap,
  TextElement,
} from '../../foundations';
import { borderRadius } from '../../foundations/borders/borderRadius';
import { spacing, spacingScaled } from '../../foundations/spacing/spacing';
import { Truncate } from '../../utils/Truncate/Truncate';
import { LoaderSpinner } from '../LoaderSpinner/LoaderSpinner';
import { IconClose } from '../icons';

interface ChipBaseProps
  extends ComponentWithSizeMode,
    ComponentWithColorMode,
    ComponentWithDisabledStateMode,
    ComponentWithActiveStateMode {
  text?: string;
  startSlot?: React.JSX.Element;
  endSlot?: React.JSX.Element;
  $loading?: boolean;
  className?: string;
}

interface ChipClickableProps extends ChipBaseProps {
  onClick?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  onDelete?: never;
}

interface ChipDeletableProps extends ChipBaseProps {
  onDelete?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  onClick?: never;
}

export type ChipProps = ChipClickableProps | ChipDeletableProps;

export const Chip: React.FC<ChipProps> = ({
  size = 'small',
  colorMode = 'neutral',
  active,
  disabled,
  text,
  startSlot,
  endSlot,
  onClick,
  onDelete,
  $loading,
  className,
}) => {
  const isDisabled = disabled || $loading;
  const colors = getComponentColors(colorMode, { noGradient: true, active, disabled: isDisabled });
  return (
    <StyledChip
      colors={colors}
      size={size}
      onClick={onClick}
      disabled={onClick ? isDisabled : undefined}
      as={!onClick ? 'span' : undefined}
      clickable={!!onClick}
      className={className}
    >
      {startSlot}
      <StyledTextElement textStyle="label" size={size}>
        <Truncate wordBreak="break-all">{text}</Truncate>
      </StyledTextElement>
      {endSlot}
      {onDelete ? (
        <StyledCloseButton type="button" colors={colors} size={size} onClick={onDelete} disabled={isDisabled}>
          {$loading ? <LoaderSpinner /> : <IconClose />}
        </StyledCloseButton>
      ) : (
        $loading && <LoaderSpinner />
      )}
    </StyledChip>
  );
};

const StyledChip = styled.button<{ colors: ComponentColors; size: ComponentSize; clickable: boolean }>`
  padding: ${({ size }) => `${spacingScaled(spacing.size4, size)}`};
  align-items: center;
  display: inline-flex;
  white-space: nowrap;
  max-width: fit-content;
  background-color: ${({ colors }) => colors.fill};
  color: ${({ colors }) => colors.on};
  border: ${({ colors, size }) => `${spacingScaled(spacing.size1, size)} solid ${colors.stroke}`};
  border-radius: ${borderRadius.roundedFull};

  & svg {
    width: ${({ size }) => lineHeightMap.label[size]};
    height: ${({ size }) => lineHeightMap.label[size]};
  }

  ${({ clickable, colors }) =>
    clickable
      ? css`
          &:hover {
            background-color: ${colors.fillFeedback};
          }
        `
      : null}
`;

const StyledTextElement = styled(TextElement)`
  padding: ${({ size }) => `0 ${spacingScaled(spacing.size4, size)}`};
`;

const StyledCloseButton = styled.button<{ colors: ComponentColors; size: ComponentSize }>`
  color: ${({ colors }) => colors.onSubjacent};
  border-radius: ${borderRadius.roundedFull};
  width: ${({ size }) => lineHeightMap.label[size]};
  height: ${({ size }) => lineHeightMap.label[size]};

  &:hover {
    background-color: ${({ colors }) => colors.fillFeedback};
  }
`;
